import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { AppError, getErrorMessage } from './errors';

/**
 * Cloud Function API Helpers
 *
 * Wraps Firebase Cloud Function calls with consistent error handling.
 *
 * Security Notes:
 * - All sensitive operations go through Cloud Functions
 * - Functions validate auth tokens server-side
 * - No secrets are exposed to the client
 */

/**
 * Generic function caller with error handling
 */
async function callFunction(functionName, data = {}) {
  try {
    const fn = httpsCallable(functions, functionName);
    const result = await fn(data);
    return result.data;
  } catch (error) {
    console.error(`Cloud Function error (${functionName}):`, error);

    // Firebase Functions errors have error.code like 'functions/internal'
    // and error.message contains our custom error code like 'STRIPE_ERROR'
    // Extract the meaningful code from either location
    const firebaseCode = error.code?.replace('functions/', '') || '';
    const customCode = error.message || error.details?.code || '';

    // Use our custom code if it matches known error codes, otherwise use firebase code
    const knownCodes = ['STRIPE_ERROR', 'AUTH_REQUIRED', 'PHONE_NOT_VERIFIED', 'USER_NOT_FOUND',
                        'RAFFLE_NOT_FOUND', 'PERMISSION_DENIED', 'INTERNAL_ERROR'];
    const code = knownCodes.includes(customCode) ? customCode :
                 knownCodes.includes(firebaseCode.toUpperCase()) ? firebaseCode.toUpperCase() :
                 'INTERNAL_ERROR';

    // Get user-friendly message
    const message = getErrorMessage({ code });

    throw new AppError(code, message);
  }
}

// ============================================
// SKILL QUESTION FUNCTIONS
// ============================================

/**
 * Get a random skill question
 *
 * Uses Cloud Function to fetch question WITHOUT the correctAnswer.
 * This prevents cheating by directly querying Firestore.
 *
 * @returns {Promise<{success: boolean, question?: Object}>}
 */
export async function getSkillQuestion() {
  return callFunction('getSkillQuestion', {});
}

// ============================================
// ENTRY FUNCTIONS
// ============================================

/**
 * Purchase raffle entry
 *
 * Creates a Stripe Checkout session for entry payment.
 * Answer validation happens server-side.
 *
 * @param {Object} params
 * @param {string} params.raffleId - Raffle to enter
 * @param {number} params.ticketCount - Number of tickets (1-10)
 * @param {string} params.skillQuestionId - Question that was answered
 * @param {number} params.skillAnswer - Index of selected answer (0, 1, or 2)
 *
 * @returns {Promise<{success: boolean, checkoutUrl?: string, error?: string}>}
 */
export async function purchaseEntry({ raffleId, ticketCount, skillQuestionId, skillAnswer }) {
  return callFunction('purchaseEntry', {
    raffleId,
    ticketCount,
    skillQuestionId,
    skillAnswer,
  });
}

/**
 * Validate skill answer (standalone)
 *
 * Used to check answer before redirecting to payment.
 * This is optional - purchaseEntry also validates.
 *
 * @param {string} questionId
 * @param {number} answer - Index of selected option
 * @returns {Promise<{correct: boolean}>}
 */
export async function validateSkillAnswer(questionId, answer) {
  return callFunction('validateSkillAnswer', {
    questionId,
    answer,
  });
}

// ============================================
// RAFFLE FUNCTIONS
// ============================================

/**
 * Create a new raffle
 *
 * @param {Object} raffleData - Raffle details (see CLAUDE.md for schema)
 * @returns {Promise<{success: boolean, raffleId?: string, checkoutUrl?: string}>}
 */
export async function createRaffle(raffleData) {
  return callFunction('createRaffle', raffleData);
}

/**
 * Get AI pricing suggestion
 *
 * @param {Object} params
 * @param {string[]} params.photos - Firebase Storage URLs
 * @param {string} params.title
 * @param {string} params.description
 * @param {string} params.category
 * @param {string} params.condition
 *
 * @returns {Promise<{success: boolean, suggestion?: Object}>}
 */
export async function getAIPricingSuggestion(params) {
  return callFunction('getAIPricingSuggestion', params);
}

// ============================================
// HANDOVER FUNCTIONS
// ============================================

/**
 * Confirm handover completion
 *
 * @param {string} raffleId
 * @param {string} role - 'winner' or 'seller'
 * @returns {Promise<{success: boolean, bothConfirmed: boolean}>}
 */
export async function confirmHandover(raffleId, role) {
  return callFunction('confirmHandover', { raffleId, role });
}

/**
 * Raise a dispute
 *
 * @param {Object} params
 * @param {string} params.raffleId
 * @param {string} params.reason - From predefined list
 * @param {string} params.description
 * @param {string[]} [params.evidence] - Storage URLs
 *
 * @returns {Promise<{success: boolean, disputeId?: string}>}
 */
export async function raiseDispute(params) {
  return callFunction('raiseDispute', params);
}

/**
 * Respond to a dispute
 *
 * @param {Object} params
 * @param {string} params.disputeId
 * @param {string} params.description
 * @param {string[]} [params.evidence] - Storage URLs
 *
 * @returns {Promise<{success: boolean}>}
 */
export async function respondToDispute(params) {
  return callFunction('respondToDispute', params);
}

/**
 * Resolve a dispute (Admin only)
 *
 * @param {Object} params
 * @param {string} params.disputeId
 * @param {string} params.resolution - 'buyerFavour', 'sellerFavour', or 'split'
 * @param {string} [params.adminNotes] - Notes about the resolution
 *
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function resolveDispute(params) {
  return callFunction('resolveDispute', params);
}

// ============================================
// CUSTOM AUTH EMAIL FUNCTIONS
// ============================================

/**
 * Send custom verification email (branded Raflr template)
 *
 * @returns {Promise<{success: boolean}>}
 */
export async function sendCustomVerificationEmail() {
  return callFunction('sendCustomVerificationEmail', {});
}

/**
 * Send custom password reset email (branded Raflr template)
 *
 * @param {Object} params
 * @param {string} params.email - User's email address
 *
 * @returns {Promise<{success: boolean}>}
 */
export async function sendCustomPasswordReset(params) {
  return callFunction('sendCustomPasswordReset', params);
}

// ============================================
// STRIPE CONNECT FUNCTIONS
// ============================================

/**
 * Create or get Stripe Connect onboarding link
 *
 * @returns {Promise<{success: boolean, url?: string}>}
 */
export async function getStripeConnectOnboardingUrl() {
  return callFunction('getStripeConnectOnboardingUrl', {});
}

/**
 * Get Stripe Connect dashboard link
 *
 * @returns {Promise<{success: boolean, url?: string}>}
 */
export async function getStripeConnectDashboardUrl() {
  return callFunction('getStripeConnectDashboardUrl', {});
}

// ============================================
// PHONE VERIFICATION FUNCTIONS
// ============================================

/**
 * Confirm phone verification with Cloud Function
 *
 * This validates that the user's phone is linked in Firebase Auth
 * before updating phoneVerified in Firestore. This is more secure
 * than allowing direct Firestore updates.
 *
 * @returns {Promise<{success: boolean, phoneNumber: string}>}
 */
export async function confirmPhoneVerification() {
  return callFunction('confirmPhoneVerification', {});
}

/**
 * Clear phone verification with Cloud Function
 *
 * This validates that the user's phone is NOT linked in Firebase Auth
 * before clearing phoneVerified in Firestore. Used when user wants
 * to change their phone number.
 *
 * @returns {Promise<{success: boolean}>}
 */
export async function clearPhoneVerification() {
  return callFunction('clearPhoneVerification', {});
}

// ============================================
// WINNER INTENT FUNCTIONS
// ============================================

/**
 * Confirm winner intent to collect
 *
 * Winner must confirm they intend to collect the item within 48 hours.
 *
 * @param {Object} params
 * @param {string} params.raffleId - The raffle ID
 *
 * @returns {Promise<{success: boolean}>}
 */
export async function confirmWinnerIntent(params) {
  return callFunction('confirmWinnerIntent', params);
}

// ============================================
// ACCOUNT DELETION FUNCTIONS
// ============================================

/**
 * Check account deletion blockers
 *
 * Returns any blockers preventing account deletion.
 *
 * @returns {Promise<{canDelete: boolean, blockers: Array}>}
 */
export async function checkDeletionBlockers() {
  return callFunction('checkDeletionBlockers', {});
}

/**
 * Delete user account
 *
 * Permanently deletes the user's account and associated data.
 * Requires explicit confirmation text.
 *
 * @param {Object} params
 * @param {string} params.confirmationText - Must be "DELETE MY ACCOUNT"
 *
 * @returns {Promise<{success: boolean, message?: string, blocked?: boolean, blockers?: Array}>}
 */
export async function deleteAccount(params) {
  return callFunction('deleteAccount', params);
}

